import React, { useEffect, useState } from 'react';
import { CloseRounded, AutoAwesomeRounded } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import {
  getTeacherAssessment,
  saveTeacherAssessment,
  regenerateTeacherAssessment,
} from '../../services/api';
import {
  TeacherAssessmentDetail,
  TeacherAssessmentSection,
  TeacherAssessmentQuestion,
} from '../../types';
import AssessmentDownloadMenu from './AssessmentDownloadMenu';

interface AssessmentEditorProps {
  token: string;
  assessmentId: number;
  onClose: () => void;
  onSaved?: () => void;
}

/** Deep-ish clone so edits never mutate the fetched object. */
const cloneSections = (sections: TeacherAssessmentSection[]): TeacherAssessmentSection[] =>
  sections.map((s) => ({
    ...s,
    questions: s.questions.map((q) => ({
      ...q,
      options: q.options ? q.options.map((o) => ({ ...o })) : q.options,
    })),
  }));

const AssessmentEditor: React.FC<AssessmentEditorProps> = ({ token, assessmentId, onClose, onSaved }) => {
  const [detail, setDetail] = useState<TeacherAssessmentDetail | null>(null);
  const [sections, setSections] = useState<TeacherAssessmentSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getTeacherAssessment(token, assessmentId);
      setDetail(d);
      setSections(cloneSections(d.sections));
    } catch (e: any) {
      setError(e.message || 'Failed to load assessment');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  const updateQuestion = (
    si: number,
    qi: number,
    patch: Partial<TeacherAssessmentQuestion>
  ) => {
    setSections((prev) => {
      const next = cloneSections(prev);
      next[si].questions[qi] = { ...next[si].questions[qi], ...patch };
      return next;
    });
  };

  const updateOption = (si: number, qi: number, oi: number, text: string) => {
    setSections((prev) => {
      const next = cloneSections(prev);
      const opts = next[si].questions[qi].options;
      if (opts) opts[oi] = { ...opts[oi], text };
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await saveTeacherAssessment(token, assessmentId, sections);
      setDetail(updated);
      setSections(cloneSections(updated.sections));
      setNotice('Saved.');
      onSaved?.();
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    if (!window.confirm('Regenerate this assessment from the topic content? Your edits will be discarded.')) return;
    setRegenerating(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await regenerateTeacherAssessment(token, assessmentId);
      setDetail(updated);
      setSections(cloneSections(updated.sections));
      setNotice('Regenerated.');
      onSaved?.();
    } catch (e: any) {
      setError(e.message || 'Failed to regenerate');
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <p className="truncate text-base font-bold text-slate-900">
              {detail?.title || 'Assessment'}
            </p>
            <p className="truncate text-xs text-slate-500">
              Grade {detail?.grade}
              {detail?.topics?.length ? ` · ${detail.topics.map((t) => t.topic_title).join(', ')}` : ''}
            </p>
          </div>
          <div className="flex flex-none items-center gap-1.5">
            {detail && sections.length > 0 && (
              <AssessmentDownloadMenu
                token={token}
                assessmentId={assessmentId}
                preview={{ ...detail, sections }}
                variant="button"
                onError={setError}
              />
            )}
            <button
              onClick={onClose}
              className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100"
              aria-label="Close"
            >
              <CloseRounded fontSize="small" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="py-10 text-center text-slate-500">Loading…</p>
          ) : error && !detail ? (
            <p className="py-10 text-center text-rose-600">{error}</p>
          ) : (
            <div className="flex flex-col gap-6">
              {sections.map((section, si) => (
                <div key={section.key}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-violet-600">
                    Section {si + 1} · {section.title}
                  </p>
                  <div className="flex flex-col gap-4">
                    {section.questions.map((q, qi) => (
                      <div key={q.id} className="rounded-xl border border-slate-200 p-3">
                        <div className="mb-2 flex items-center gap-2">
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-500">
                            {q.difficulty}
                          </span>
                          <span className="text-[10px] uppercase text-slate-400">{q.kind}</span>
                        </div>
                        <textarea
                          className="w-full resize-y rounded-lg border border-slate-300 p-2 text-sm"
                          rows={2}
                          value={q.question}
                          onChange={(e) => updateQuestion(si, qi, { question: e.target.value })}
                        />

                        {q.kind === 'mcq' && q.options ? (
                          <div className="mt-2 flex flex-col gap-1.5">
                            {q.options.map((opt, oi) => (
                              <label key={opt.id} className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name={`correct-${q.id}`}
                                  checked={q.correct_option_id === opt.id}
                                  onChange={() => updateQuestion(si, qi, { correct_option_id: opt.id })}
                                />
                                <span className="w-4 text-xs font-semibold text-slate-400">{opt.id}.</span>
                                <input
                                  className="flex-1 rounded-lg border border-slate-300 px-2 py-1 text-sm"
                                  value={opt.text}
                                  onChange={(e) => updateOption(si, qi, oi, e.target.value)}
                                />
                              </label>
                            ))}
                            <input
                              className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs"
                              placeholder="Explanation"
                              value={q.explanation || ''}
                              onChange={(e) => updateQuestion(si, qi, { explanation: e.target.value })}
                            />
                          </div>
                        ) : (
                          <div className="mt-2 flex flex-col gap-1.5">
                            <textarea
                              className="w-full resize-y rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs"
                              rows={2}
                              placeholder="Model answer (used to auto-grade)"
                              value={q.model_answer || ''}
                              onChange={(e) => updateQuestion(si, qi, { model_answer: e.target.value })}
                            />
                            <input
                              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs"
                              placeholder="Rubric (key points)"
                              value={q.rubric || ''}
                              onChange={(e) => updateQuestion(si, qi, { rubric: e.target.value })}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-5 py-3">
          <button
            onClick={regenerate}
            disabled={regenerating || saving || loading}
            className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-violet-600 hover:bg-violet-50 disabled:opacity-50"
          >
            {regenerating ? (
              <CircularProgress size={16} sx={{ color: '#7c3aed' }} />
            ) : (
              <AutoAwesomeRounded fontSize="small" />
            )}
            {regenerating ? 'Regenerating…' : 'Regenerate'}
          </button>
          <div className="flex items-center gap-3">
            {notice && <span className="text-xs text-emerald-600">{notice}</span>}
            {error && detail && <span className="text-xs text-rose-600">{error}</span>}
            <button onClick={onClose} className="rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100">
              Close
            </button>
            <button
              onClick={save}
              disabled={saving || loading}
              className="flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
            >
              {saving && <CircularProgress size={14} sx={{ color: 'white' }} />}
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentEditor;
